import { App } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState, type UIEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { SpecialFolderTypeEnum } from '../../enums/SpecialFolderTypeEnum';
import type File from '../../interfaces/File';
import type { GetFilesPayload } from '../../interfaces/FileApiService';
import { FileService } from '../../services/fileApiService';
import { useAppStore } from '../../store/store';
import { handleFilePasswordError } from '../../utils/filePasswordErrors';

const fileService = new FileService();

const DEFAULT_FILTERS_BASE: Omit<GetFilesPayload, 'list' | 'type' | 'parent' | 'offset'> & {
  parent?: string;
  offset: number;
} = {
  count: 25,
  fileTypes: [],
  onlySigned: false,
  onlyUnsealed: false,
  orderAsc: true,
  orderColumn: 0,
  priceFilter: 0,
  search: '',
  shareWith: [],
  tags: [],
  parent: undefined,
  offset: 0,
};

export function getDefaultFiltersForListType(
  listType: 'personal' | 'received' | 'favorites',
): GetFilesPayload {
  return {
    ...DEFAULT_FILTERS_BASE,
    list: listType === 'favorites' ? 2 : 0,
    type: listType === 'received' ? 1 : 2,
    offset: 0,
    parent: undefined,
  };
}

export interface UseFileShortcutFormParams {
  /** Optional when reused as a generic destination folder picker. */
  selectedFile?: File | null;
  currentFolder?: File | null;
}

export function useFileShortcutForm({ selectedFile, currentFolder }: UseFileShortcutFormParams) {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    authenticatedUser,
    getFilePasswordSession,
    setPendingFilePasswordRequest,
    setPendingFilePasswordFromMoveForm,
    setPendingFilePasswordFromUpload,
  } = useAppStore();

  // No selected file → treat as "mine" so destination picker opens personal roots freely.
  const isSelectedFileMine = useMemo(
    () => !selectedFile || selectedFile.ownerId === authenticatedUser?.id,
    [selectedFile, authenticatedUser],
  );

  const [selectedListType, setSelectedListType] = useState<'personal' | 'received' | 'favorites'>(
    'personal',
  );

  // Prefill: open already inside `currentFolder` (selected as destination), like Move received start.
  const [filters, setFilters] = useState<GetFilesPayload>(() => {
    const base = getDefaultFiltersForListType('personal');
    if (currentFolder?.id != null) {
      return { ...base, parent: String(currentFolder.id) };
    }
    return base;
  });

  const [files, setFiles] = useState<File[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<File | null>(() =>
    currentFolder?.id != null ? currentFolder : null,
  );
  const [parent, setParent] = useState<File | undefined>();
  const [isNavigatingIntoFolder, setIsNavigatingIntoFolder] = useState(
    () => currentFolder?.id != null,
  );
  const [isFilesRootSelected, setIsFilesRootSelected] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  const previousParentRef = useRef<string | undefined>(undefined);
  const targetFolderIdRef = useRef<number | null>(null);
  const folderToSelectAfterPasswordRef = useRef<File | null>(null);
  const fromApiErrorRef = useRef<boolean>(false);
  const hadPendingRequestRef = useRef(false);
  /** Bumps on each `getFiles` call so out-of-order responses cannot overwrite paginated state. */
  const filesFetchGenerationRef = useRef(0);
  /** Synchronous guard: blocks duplicate offset bumps before `isLoading` flips true. */
  const loadMoreLockedRef = useRef(false);

  const selectedFileId = selectedFile?.id;

  const listFetchKey = useMemo(
    () => `${selectedFileId}:${filters.parent ?? ''}:${selectedListType}`,
    [selectedFileId, filters.parent, selectedListType],
  );

  const memoizedListFilters = useMemo(
    () => ({
      count: filters.count,
      offset: filters.offset,
      orderAsc: filters.orderAsc,
      orderColumn: filters.orderColumn,
      parent: filters.parent,
    }),
    [filters.count, filters.offset, filters.orderAsc, filters.orderColumn, filters.parent],
  );

  const openFolderOrRequestPassword = useCallback(
    (f: File) => {
      if (!f.isFolder) return;
      if (f.isPasswordSet && !f.isPasswordBypass && !getFilePasswordSession(f.id)) {
        folderToSelectAfterPasswordRef.current = null;
        targetFolderIdRef.current = f.id;
        fromApiErrorRef.current = false;
        previousParentRef.current = filters.parent;
        setPendingFilePasswordFromMoveForm(true);
        setPendingFilePasswordRequest({
          fileId: f.id,
          fileName: f.fileName,
        });
        return;
      }
      previousParentRef.current = filters.parent;
      setSelectedFolder(f);
      setIsFilesRootSelected(false);
      setFilters((prev) => ({ ...prev, search: '', offset: 0, parent: f.id.toString() }));
    },
    [
      filters,
      getFilePasswordSession,
      setPendingFilePasswordRequest,
      setPendingFilePasswordFromMoveForm,
    ],
  );

  const selectFolderOrRequestPassword = useCallback(
    (f: File) => {
      if (!f.isFolder) return;
      if (f.isPasswordSet && !f.isPasswordBypass && !getFilePasswordSession(f.id)) {
        folderToSelectAfterPasswordRef.current = { ...f, path: parent?.path };
        targetFolderIdRef.current = f.id;
        fromApiErrorRef.current = false;
        setPendingFilePasswordFromMoveForm(true);
        setPendingFilePasswordRequest({
          fileId: f.id,
          fileName: f.fileName,
        });
        return;
      }
      setSelectedFolder({ ...f, path: parent?.path });
      setIsFilesRootSelected(false);
    },
    [
      parent?.path,
      getFilePasswordSession,
      setPendingFilePasswordRequest,
      setPendingFilePasswordFromMoveForm,
    ],
  );

  const handleScroll = useCallback(
    (event?: UIEvent<HTMLElement>) => {
      const scrollElement = event?.currentTarget ?? listRef.current;
      if (!scrollElement) return;
      const { scrollTop, scrollHeight, clientHeight } = scrollElement;
      const nearBottom = scrollTop + clientHeight >= scrollHeight - 50;
      if (nearBottom && hasMore && !isLoading && !loadMoreLockedRef.current) {
        loadMoreLockedRef.current = true;
        setFilters((prev) => ({ ...prev, offset: prev.offset + prev.count }));
      }
    },
    [isLoading, hasMore],
  );

  const isAtRootLevel = useMemo(() => {
    return (!filters.parent || filters.parent === '') && !isNavigatingIntoFolder;
  }, [filters.parent, isNavigatingIntoFolder]);

  useEffect(() => {
    if (!isSelectedFileMine && isAtRootLevel) {
      setSelectedFolder(null);
      setIsNavigatingIntoFolder(false);
      setIsFilesRootSelected(false);
      setFilters((prevFilters) => ({
        ...prevFilters,
        offset: 0,
        parent: undefined,
        list: 0,
      }));
    }
  }, [selectedListType, isSelectedFileMine, isAtRootLevel]);

  /**
   * Loads folder pages for the shortcut picker. A single effect keyed on list scope + offset
   * avoids split reset/fetch races that skipped or reset paginated results.
   */
  useEffect(() => {
    if (isAtRootLevel) {
      setFiles([]);
      setParent(undefined);
      setHasMore(false);
      loadMoreLockedRef.current = false;
      return;
    }

    const fetchGeneration = ++filesFetchGenerationRef.current;
    const requestOffset = memoizedListFilters.offset;
    if (requestOffset === 0) {
      setFiles([]);
      setHasMore(false);
    }
    const parentForRequest = memoizedListFilters.parent;
    const hasParent = !!(parentForRequest && parentForRequest.trim() !== '');
    const payloadList = hasParent ? 0 : selectedListType === 'favorites' ? 2 : 0;
    const searchQuery = (filters.search ?? '').trim();
    const payload: GetFilesPayload = {
      ...filters,
      ...memoizedListFilters,
      type: selectedListType === 'received' ? 1 : 2,
      list: payloadList,
      parent: parentForRequest || undefined,
      search: searchQuery || undefined,
    };

    setIsLoading(true);
    void fileService
      .getFiles(payload)
      .then((res) => {
        if (fetchGeneration !== filesFetchGenerationRef.current) {
          return;
        }
        const page = res.myFiles ?? [];
        setFiles((prevFiles) => {
          const allFiles = requestOffset === 0 ? page : [...prevFiles, ...page];
          const filesById = new Map<number, File>();
          allFiles.forEach((file) => filesById.set(file.id, file));
          return Array.from(filesById.values());
        });
        setParent(res?.parent || undefined);
        setHasMore(requestOffset + page.length < (res.total ?? 0));
      })
      .catch((error) => {
        if (fetchGeneration !== filesFetchGenerationRef.current) {
          return;
        }
        if (handleFilePasswordError(error)) {
          fromApiErrorRef.current = true;
          setPendingFilePasswordFromMoveForm(true);
          setFiles([]);
          setParent(undefined);
          return;
        }
        notification.error({
          title: t('errors.genericFetchError', {
            resource: t('common.files'),
          }),
        });
      })
      .finally(() => {
        if (fetchGeneration === filesFetchGenerationRef.current) {
          setIsLoading(false);
          loadMoreLockedRef.current = false;
        }
      });
  }, [
    isAtRootLevel,
    listFetchKey,
    memoizedListFilters,
    selectedListType,
    filters.count,
    filters.fileTypes,
    filters.onlySigned,
    filters.onlyUnsealed,
    filters.orderAsc,
    filters.orderColumn,
    filters.priceFilter,
    filters.search,
    filters.shareWith,
    filters.tags,
    notification,
    setPendingFilePasswordFromMoveForm,
    t,
  ]);

  const pendingFilePasswordRequest = useAppStore((s) => s.pendingFilePasswordRequest);

  useEffect(() => {
    if (pendingFilePasswordRequest) {
      hadPendingRequestRef.current = true;
    } else if (hadPendingRequestRef.current) {
      hadPendingRequestRef.current = false;
      const fromUpload = useAppStore.getState().pendingFilePasswordFromUpload;
      if (fromUpload) {
        folderToSelectAfterPasswordRef.current = null;
        targetFolderIdRef.current = null;
        fromApiErrorRef.current = false;
        setPendingFilePasswordFromUpload(false);
        setPendingFilePasswordFromMoveForm(false);
        return;
      }
      const targetId = targetFolderIdRef.current;
      const fromApi = fromApiErrorRef.current;
      const folderToSelect = folderToSelectAfterPasswordRef.current;

      if (targetId != null && getFilePasswordSession(targetId)) {
        if (folderToSelect != null && folderToSelect.id === targetId) {
          setSelectedFolder(folderToSelect);
          folderToSelectAfterPasswordRef.current = null;
        } else {
          setFilters((prev) => ({ ...prev, offset: 0, parent: String(targetId) }));
        }
      } else if (
        fromApi &&
        filters.parent &&
        getFilePasswordSession(parseInt(String(filters.parent), 10))
      ) {
        setFiles([]);
        setFilters((prev) => ({ ...prev, offset: 0, parent: filters.parent }));
      } else {
        const revertParent = fromApi ? undefined : previousParentRef.current;
        setFilters((prev) => ({ ...prev, offset: 0, parent: revertParent }));
        setSelectedFolder(null);
        if (!revertParent || revertParent === '') {
          setIsNavigatingIntoFolder(false);
          setIsFilesRootSelected(false);
        }
      }

      folderToSelectAfterPasswordRef.current = null;
      targetFolderIdRef.current = null;
      fromApiErrorRef.current = false;
      setPendingFilePasswordFromMoveForm(false);
    }
  }, [
    pendingFilePasswordRequest,
    filters.parent,
    getFilePasswordSession,
    setPendingFilePasswordFromMoveForm,
    setPendingFilePasswordFromUpload,
  ]);

  const currentFolderParent = useMemo(() => {
    if (parent?.path && parent.path.length > 0) {
      const path = parent.path;
      return {
        ...path[path.length - 2],
        title: path?.[path.length - 1]?.title,
      };
    }
    return null;
  }, [parent]);

  const isFileDisabled = useCallback(
    (file: File) => {
      if (
        [SpecialFolderTypeEnum.QuickShare, SpecialFolderTypeEnum.AiGrant].includes(
          file.specialFolder,
        ) ||
        selectedFileId === file.id
      ) {
        return true;
      }
      if (!file.isFolder) {
        return true;
      }
      if (selectedListType === 'received' && file.isReadOnly) {
        return true;
      }
      if (selectedListType === 'personal' && file.isReadOnly) {
        return true;
      }
      return false;
    },
    [selectedFileId, selectedListType],
  );

  const currentPositionLabel = useMemo(() => {
    if (currentFolder?.fileName) {
      return currentFolder.fileName;
    }
    if (isAtRootLevel && !isSelectedFileMine) {
      return t('modal.fileShortcutForm.selectDestination', {
        defaultValue: t('common.select'),
      });
    }
    if (selectedListType === 'received') {
      return t('common.received');
    }
    if (selectedListType === 'favorites') {
      return t('common.favorites');
    }
    return t('common.files');
  }, [currentFolder, selectedListType, t, isAtRootLevel, isSelectedFileMine]);

  const handleSearchChange = useCallback(
    (search: string) => {
      // Avoid empty-state flash before fetch effect runs (client filter can empty list first).
      if (!isAtRootLevel) {
        setIsLoading(true);
      }
      setFilters((prev) => ({ ...prev, search, offset: 0 }));
    },
    [isAtRootLevel],
  );

  return {
    t,
    isLoading,
    selectedListType,
    setSelectedListType,
    filters,
    setFilters,
    files,
    selectedFolder,
    setSelectedFolder,
    listRef,
    isNavigatingIntoFolder,
    setIsNavigatingIntoFolder,
    isFilesRootSelected,
    setIsFilesRootSelected,
    isAtRootLevel,
    isSelectedFileMine,
    currentFolderParent,
    openFolderOrRequestPassword,
    selectFolderOrRequestPassword,
    isFileDisabled,
    currentPositionLabel,
    handleScroll,
    parent,
    authenticatedUser,
    handleSearchChange,
  };
}
